import * as React from 'react';
import { Keyboard, ScrollView, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedRef, useSharedValue, useDerivedValue, useAnimatedScrollHandler, useAnimatedReaction, runOnJS, } from 'react-native-reanimated';
import { AppView, NavigationHeader, rem, Spacer, StatusBar } from 'design-system';
import SelectorControllableReanimated from 'design-system/SelectorControllableReanimated';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectFundInfo, selectGeoInfo } from '../reducers/selectors';
import DiscoverCategoryList from '../components/DiscoverCategoryList';
import FundPickerFloatingBar from '../components/FundPickerFloatingBar';
import useFundPickerStore from '../hooks/useFundPickerStore';
import { isWeb } from '../../../constants';
const InvestDiscoverCategorizationDetails = ({ route, navigation, }) => {
    const colors = useColors();
    const { active: fundPickerMode } = useFundPickerStore();
    const { type, initialSelected } = route.params;
    const scrollX = useSharedValue(0);
    const selectorScrollViewRef = React.useRef(null);
    const horizontalScrollViewRef = useAnimatedRef();
    const data = useAppSelector((state) => (type === 'theme' ? selectFundInfo(state) : selectGeoInfo(state)));
    const { contentWidth, paddingHorizontal } = useAppFrameDimensions(isWeb);
    // On web, flex lets each page's content stretch or shrink it, so the pager would snap
    // between pages and the tab pill would sit off its label. Each page takes exactly one width.
    const pageStyle = React.useMemo(() => [styles.item, isWeb && { width: contentWidth }], [contentWidth]);
    const initialIndex = React.useMemo(() => {
        const index = data.findIndex((item) => item.fundInfo.title === initialSelected);
        return index >= 0 ? index : 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const currentIndex = useDerivedValue(() => Math.round(scrollX.value / contentWidth), [scrollX, contentWidth]);
    const combinedScrollY = useSharedValue({
        [initialIndex]: 0,
    });
    const scrollY = useDerivedValue(() => combinedScrollY.value[currentIndex.value] || 0, [combinedScrollY, currentIndex]);
    React.useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const scrollXHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollX.value = contentOffset.x;
    }, []);
    const sections = React.useMemo(() => data.map((item) => ({
        ...item,
        id: item.fundInfo.title,
        name: item.fundInfo.title,
        text: item.fundInfo.title,
        width: item.fundInfo.titleWidth,
    })), [data]);
    React.useEffect(() => {
        const timeout = setTimeout(() => {
            horizontalScrollViewRef.current?.scrollTo({
                y: 0,
                animated: false,
                x: initialIndex * contentWidth,
            });
        }, 10);
        return () => {
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onSelectView = React.useCallback((index) => {
        Keyboard.dismiss();
        horizontalScrollViewRef.current?.scrollTo({
            y: 0,
            animated: true,
            x: index * contentWidth,
        });
    }, [horizontalScrollViewRef, contentWidth]);
    const centerOnItem = React.useCallback((index) => {
        if (selectorScrollViewRef.current && sections[index]) {
            const item = sections[index];
            const itemsX = sections.slice(0, index).reduce((acc, sec) => acc + sec.width + rem(4), 0);
            const itemWidth = item.width;
            const scrollViewWidth = contentWidth - (paddingHorizontal + rem(16)) * 2;
            let scrollToX = itemsX - scrollViewWidth / 2 + itemWidth / 2;
            const totalContentWidth = sections.reduce((acc, sec) => acc + sec.width, 0) + Math.max(0, sections.length - 1) * rem(4);
            const maxScrollX = Math.max(0, totalContentWidth - scrollViewWidth + rem(32));
            scrollToX = Math.max(0, Math.min(scrollToX, maxScrollX));
            selectorScrollViewRef.current.scrollTo({
                x: scrollToX,
                animated: true,
            });
        }
    }, [sections, contentWidth, paddingHorizontal]);
    useAnimatedReaction(() => Math.round(scrollX.value / contentWidth), (currentPage, previousPage) => {
        if (currentPage !== previousPage) {
            runOnJS(centerOnItem)(currentPage);
        }
    }, [contentWidth]);
    return (<AppView withFrame={false} type="secondary">
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader isInTab title={type === 'theme' ? 'Themes' : 'Regions'} scrollY={scrollY}>
        <Spacer h={8}/>
        <ScrollView horizontal ref={selectorScrollViewRef} showsHorizontalScrollIndicator={false} contentContainerStyle={{
            paddingHorizontal: paddingHorizontal + rem(16),
        }}>
          <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={contentWidth} setSelection={onSelectView} selectorStyle={styles.selectorStyle} itemContainerStyle={styles.itemContainerStyle}/>
        </ScrollView>
        <Spacer h={8}/>
      </NavigationHeader>
      <Animated.ScrollView horizontal pagingEnabled scrollEnabled decelerationRate="fast" scrollEventThrottle={16} onScroll={scrollXHandler} ref={horizontalScrollViewRef} showsHorizontalScrollIndicator={false} contentContainerStyle={{ width: data.length * contentWidth }}>
        {data.map((eachData, index) => (<View key={eachData.fundInfo.title} style={pageStyle}>
            <DiscoverCategoryList index={index} assets={eachData.fundAssets} combinedScrollY={combinedScrollY} isInitial={index === initialIndex}/>
          </View>))}
      </Animated.ScrollView>
      {fundPickerMode ? <FundPickerFloatingBar popCount={2}/> : null}
    </AppView>);
};
export default InvestDiscoverCategorizationDetails;
const styles = StyleSheet.create({
    item: {
        flex: 1,
    },
    itemContainerStyle: {
        margin: 0,
        padding: 0,
    },
    selectorStyle: {
        margin: 0,
        padding: 0,
        borderRadius: rem(8),
        justifyContent: 'center',
    },
});
